import React, { useRef } from 'react';

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Called once every box is filled (typed, pasted or autofilled). */
  onComplete?: (value: string) => void;
  length?: number;
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
}

/**
 * One box per digit of a one-time code. Typing moves to the next box,
 * Backspace to the previous one, and pasting (or the phone's SMS/email code
 * autofill) spreads the digits across the boxes.
 */
const OtpInput: React.FC<OtpInputProps> = ({
  value,
  onChange,
  onComplete,
  length = 6,
  disabled,
  invalid,
  autoFocus,
}) => {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = Array.from({ length }, (_, i) => value[i] ?? '');

  const focus = (i: number) => refs.current[Math.max(0, Math.min(length - 1, i))]?.focus();

  // Write `input` digits starting at box `index` (never leaving gaps).
  const fill = (index: number, input: string) => {
    const clean = input.replace(/\D/g, '');
    if (!clean) return;
    const start = Math.min(index, value.length);
    const next = (value.slice(0, start) + clean + value.slice(start + clean.length)).slice(0, length);
    onChange(next);
    focus(start + clean.length);
    // Submit when the code becomes complete, or when retyping a full code
    // reaches the last box — not on every digit edited inside a full code.
    if (next.length === length && (value.length < length || start + clean.length >= length)) {
      onComplete?.(next);
    }
  };

  const handleChange = (i: number, e: React.ChangeEvent<HTMLInputElement>) => {
    let typed = e.target.value.replace(/\D/g, '');
    // A filled box receives its old digit plus the new one — keep the new one.
    if (typed.length === 2 && digits[i]) typed = typed.replace(digits[i], '');
    fill(i, typed);
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const at = digits[i] ? i : i - 1;
      if (at < 0) return;
      onChange(value.slice(0, at) + value.slice(at + 1));
      focus(at);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focus(i - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focus(i + 1);
    }
  };

  const handlePaste = (i: number, e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    fill(i, e.clipboardData.getData('text'));
  };

  return (
    <div className="flex justify-between" style={{ gap: 8 }}>
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          aria-label={`Digit ${i + 1}`}
          autoFocus={autoFocus && i === 0}
          disabled={disabled}
          value={digit}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={(e) => handlePaste(i, e)}
          onFocus={(e) => e.target.select()}
          className="text-center font-extrabold text-ink outline-none transition-colors focus:ring-4 focus:ring-[rgba(225,1,116,0.15)]"
          style={{
            flex: 1,
            minWidth: 0,
            maxWidth: 54,
            height: 56,
            fontSize: 24,
            borderRadius: 12,
            border: `1.5px solid ${invalid ? 'var(--red)' : digit ? 'var(--brand)' : 'var(--g200)'}`,
            background: digit ? 'var(--brand-light)' : '#fff',
          }}
        />
      ))}
    </div>
  );
};

export default OtpInput;
